import type { FC, ReactNode } from 'react';
import { Link as LinkDoRouter } from 'react-router-dom';
import {
  Button, Checkbox, Form, FormGroup, InlineLoading, InlineNotification, Link,
  PasswordInput, Stack, TextInput,
} from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';

import { PaginaDeAuth } from '../../../padroes/PaginaDeAuth';
import { CampoDeNascimento } from '../CampoDeNascimento';
import { EmailEscolhido } from '../EmailEscolhido';
import { EntrarComProvedor, type PropsDosProvedores } from '../EntrarComProvedor';
import type { CampoDoCadastro, ErrosDoCadastro } from '../../../../logica/auth/useCadastro';
import { ROTULOS, rotuloDoAviso } from '../../../padroes/rotulos';
import styles from '../auth.module.scss';

// CRIAR CONTA — o mesmo padrão do login, em dois passos: primeiro o e-mail e o "Continuar" (com as
// outras formas de entrar à vista); depois o e-mail escolhido com o "Trocar", o nome, a senha, a
// data de nascimento e os dois aceites (nenhum nasce marcado; comunicações é opcional e separado do
// aceite legal) e o primário. Depois do envio, o passo do código na mesma página.

export interface PropsDoCadastro {
  step: 'form' | 'code';
  etapa: 'email' | 'dados';
  continuar: () => void;
  trocarEmail: () => void;
  name: string; setName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  birthDate: string; setBirthDate: (v: string) => void;
  aceite: boolean; setAceite: (v: boolean) => void;
  comunicacoes: boolean; setComunicacoes: (v: boolean) => void;
  loading: boolean;
  erros: ErrosDoCadastro;
  validarCampo: (c: CampoDoCadastro) => void;
  error: string | null;
  cadastrar: () => void;
  idadeMinima: number;
  codigo: ReactNode;
  provedores: PropsDosProvedores;
}

export const Cadastro: FC<PropsDoCadastro> = (p) => {
  if (p.step === 'code') {
    return <PaginaDeAuth titulo='Confirme seu e-mail'>{p.codigo}</PaginaDeAuth>;
  }

  return (
    <PaginaDeAuth
      titulo='Criar sua conta'
      apoio={<>Já tem conta? <Link as={LinkDoRouter} to='/login'>Entrar</Link></>}
    >
      <Form noValidate onSubmit={(e) => { e.preventDefault(); if (p.etapa === 'email') p.continuar(); else p.cadastrar(); }}>
        {p.etapa === 'email' ? (
          <Stack gap={6}>
            <TextInput
              id='cadastro-email'
              type='email'
              labelText='E-mail'
              autoComplete='email'
              value={p.email}
              onChange={(e) => p.setEmail(e.target.value)}
              invalid={!!p.erros.email}
              invalidText={p.erros.email}
              autoFocus
            />
            <div className={styles.acoes}>
              <Button type='submit' renderIcon={ArrowRight}>Continuar</Button>
            </div>
          </Stack>
        ) : (
        <Stack gap={6}>
          <EmailEscolhido rotulo='Criando a conta de' email={p.email.trim()} trocar={p.trocarEmail} />
          <TextInput
            id='cadastro-nome'
            labelText='Seu nome'
            autoComplete='name'
            value={p.name}
            onChange={(e) => p.setName(e.target.value)}
            onBlur={() => p.validarCampo('nome')}
            invalid={!!p.erros.nome}
            invalidText={p.erros.nome}
            autoFocus
          />
          <PasswordInput
            id='cadastro-senha'
            labelText='Senha'
            helperText='Mínimo de 6 caracteres.'
            autoComplete='new-password'
            showPasswordLabel='Mostrar senha'
            hidePasswordLabel='Ocultar senha'
            value={p.password}
            onChange={(e) => p.setPassword(e.target.value)}
            onBlur={() => p.validarCampo('senha')}
            invalid={!!p.erros.senha}
            invalidText={p.erros.senha}
          />
          <CampoDeNascimento
            id='cadastro-nascimento'
            valor={p.birthDate}
            aoMudar={p.setBirthDate}
            aoSair={() => p.validarCampo('nascimento')}
            idadeMinima={p.idadeMinima}
            invalido={p.erros.nascimento}
          />
          <FormGroup legendText='Declarações e aceites'>
            <Stack gap={4}>
              <Checkbox
                id='cadastro-aceite'
                checked={p.aceite}
                onChange={(_, { checked }) => p.setAceite(checked)}
                invalid={!!p.erros.aceite}
                invalidText={p.erros.aceite}
                labelText={(
                  <>
                    Declaro ter {p.idadeMinima} anos ou mais e aceito os{' '}
                    <Link href='/legal/termos' target='_blank' rel='noreferrer' inline>Termos de Uso</Link> e a{' '}
                    <Link href='/legal/privacidade' target='_blank' rel='noreferrer' inline>Política de Privacidade</Link>.
                  </>
                )}
              />
              <Checkbox
                id='cadastro-comunicacoes'
                checked={p.comunicacoes}
                onChange={(_, { checked }) => p.setComunicacoes(checked)}
                labelText='Quero receber novidades da Maestra por e-mail (opcional).'
              />
            </Stack>
          </FormGroup>
          {p.error && <InlineNotification kind='error' lowContrast hideCloseButton title={p.error} statusIconDescription={rotuloDoAviso('error')} aria-label={ROTULOS.fecharAviso} />}
          <div className={styles.acoes}>
            {p.loading
              ? <InlineLoading description='Criando a conta…' iconDescription={ROTULOS.carregando} />
              : <Button type='submit' renderIcon={ArrowRight}>Criar conta</Button>}
          </div>
        </Stack>
        )}
      </Form>
      <EntrarComProvedor {...p.provedores} />
    </PaginaDeAuth>
  );
};
